<script>
	import { onMount, onDestroy } from "svelte";
	import Icon from "./icons/Icon.svelte";

	export let show = false;
	export let imageSrc = "";
	export let title = "Enquadrar Foto do Produto";
	export let onCrop = (croppedBase64) => {};
	export let onCancel = () => {};
	export let onUseOriginal = () => {};

	let canvasRef;
	let containerRef;
	let zoom = 1;
	let minZoom = 1;
	let maxZoom = 3;
	let panX = 0;
	let panY = 0;
	let isDragging = false;
	let dragStartX = 0;
	let dragStartY = 0;
	let initialPanX = 0;
	let initialPanY = 0;

	let imgElement = null;
	let imgNaturalWidth = 0;
	let imgNaturalHeight = 0;
	let isImageLoaded = false;

	const CROP_BOX_SIZE = 280; // Tamanho visual da área de enquadramento em pixels
	const OUTPUT_SIZE = 600;   // Resolução final padronizada exportada

	$: if (show && imageSrc) {
		loadImage(imageSrc);
	}

	function loadImage(src) {
		isImageLoaded = false;
		imgElement = new Image();
		imgElement.crossOrigin = "anonymous";
		imgElement.onload = () => {
			imgNaturalWidth = imgElement.naturalWidth || imgElement.width;
			imgNaturalHeight = imgElement.naturalHeight || imgElement.height;
			
			// Calcula zoom inicial para cobrir o quadrado completamente
			const scaleX = CROP_BOX_SIZE / imgNaturalWidth;
			const scaleY = CROP_BOX_SIZE / imgNaturalHeight;
			minZoom = Math.max(scaleX, scaleY);
			zoom = minZoom;
			maxZoom = minZoom * 3.5;

			// Centraliza inicialmente
			panX = (CROP_BOX_SIZE - imgNaturalWidth * zoom) / 2;
			panY = (CROP_BOX_SIZE - imgNaturalHeight * zoom) / 2;
			
			isImageLoaded = true;
		};
		imgElement.src = src;
	}

	function handleMouseDown(e) {
		if (!isImageLoaded) return;
		isDragging = true;
		dragStartX = e.clientX;
		dragStartY = e.clientY;
		initialPanX = panX;
		initialPanY = panY;
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
	}

	function handleMouseMove(e) {
		if (!isDragging) return;
		const dx = e.clientX - dragStartX;
		const dy = e.clientY - dragStartY;
		updatePan(initialPanX + dx, initialPanY + dy);
	}

	function handleMouseUp() {
		isDragging = false;
		window.removeEventListener("mousemove", handleMouseMove);
		window.removeEventListener("mouseup", handleMouseUp);
	}

	// Suporte a Touch em telas móveis
	function handleTouchStart(e) {
		if (!isImageLoaded || e.touches.length !== 1) return;
		isDragging = true;
		dragStartX = e.touches[0].clientX;
		dragStartY = e.touches[0].clientY;
		initialPanX = panX;
		initialPanY = panY;
	}

	function handleTouchMove(e) {
		if (!isDragging || e.touches.length !== 1) return;
		const dx = e.touches[0].clientX - dragStartX;
		const dy = e.touches[0].clientY - dragStartY;
		updatePan(initialPanX + dx, initialPanY + dy);
	}

	function handleTouchEnd() {
		isDragging = false;
	}

	function updatePan(newX, newY) {
		const currentW = imgNaturalWidth * zoom;
		const currentH = imgNaturalHeight * zoom;

		// Limites suaves para a imagem não fugir da caixa de corte
		const minX = Math.min(0, CROP_BOX_SIZE - currentW);
		const maxX = Math.max(0, (CROP_BOX_SIZE - currentW) / 2);
		const minY = Math.min(0, CROP_BOX_SIZE - currentH);
		const maxY = Math.max(0, (CROP_BOX_SIZE - currentH) / 2);

		panX = Math.min(maxX, Math.max(minX, newX));
		panY = Math.min(maxY, Math.max(minY, newY));
	}

	function handleZoomChange(newZoom) {
		const oldZoom = zoom;
		zoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

		// Ajusta o pan para dar zoom em torno do centro do crop box
		const centerOffsetFactor = (zoom / oldZoom);
		const centerX = CROP_BOX_SIZE / 2;
		const centerY = CROP_BOX_SIZE / 2;

		const newPanX = centerX - (centerX - panX) * centerOffsetFactor;
		const newPanY = centerY - (centerY - panY) * centerOffsetFactor;

		updatePan(newPanX, newPanY);
	}

	function handleWheel(e) {
		e.preventDefault();
		const delta = e.deltaY < 0 ? 0.08 : -0.08;
		handleZoomChange(zoom + delta * minZoom);
	}

	function handleReset() {
		if (!isImageLoaded) return;
		zoom = minZoom;
		panX = (CROP_BOX_SIZE - imgNaturalWidth * zoom) / 2;
		panY = (CROP_BOX_SIZE - imgNaturalHeight * zoom) / 2;
	}

	function executeCrop() {
		if (!isImageLoaded || !imgElement) return;

		const canvas = document.createElement("canvas");
		canvas.width = OUTPUT_SIZE;
		canvas.height = OUTPUT_SIZE;
		const ctx = canvas.getContext("2d");

		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = "high";

		// Mapeia coordenadas visuais da caixa (CROP_BOX_SIZE) para o canvas final (OUTPUT_SIZE)
		const ratio = OUTPUT_SIZE / CROP_BOX_SIZE;
		const drawX = panX * ratio;
		const drawY = panY * ratio;
		const drawW = imgNaturalWidth * zoom * ratio;
		const drawH = imgNaturalHeight * zoom * ratio;

		ctx.drawImage(imgElement, drawX, drawY, drawW, drawH);

		// Exporta em WebP com alta qualidade (ou PNG como fallback)
		let croppedDataUrl = "";
		try {
			croppedDataUrl = canvas.toDataURL("image/webp", 0.92);
		} catch (err) {
			croppedDataUrl = canvas.toDataURL("image/png");
		}

		if (onCrop) onCrop(croppedDataUrl);
	}

	onDestroy(() => {
		if (typeof window !== "undefined") {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		}
	});
</script>

{#if show}
	<div class="cropper-backdrop" on:click|self={onCancel} role="dialog" aria-modal="true">
		<div class="cropper-modal-card">
			<div class="cropper-header">
				<div class="header-title-row">
					<div class="crop-icon-box">
						<Icon name="camera" size={18} color="#FF6A00" />
					</div>
					<div>
						<h3 class="crop-title">{title}</h3>
						<p class="crop-subtitle">Arraste para posicionar e use o zoom para enquadrar a foto</p>
					</div>
				</div>
				<button type="button" class="btn-cropper-close" on:click={onCancel} title="Fechar">
					<Icon name="x" size={18} />
				</button>
			</div>

			<div class="cropper-body">
				<!-- Área de visualização e corte interativa -->
				<div 
					class="crop-stage"
					bind:this={containerRef}
					on:mousedown={handleMouseDown}
					on:touchstart={handleTouchStart}
					on:touchmove={handleTouchMove}
					on:touchend={handleTouchEnd}
					on:wheel={handleWheel}
				>
					{#if isImageLoaded}
						<img 
							src={imageSrc} 
							alt="Prévia para corte"
							class="crop-draggable-img"
							style="
								width: {imgNaturalWidth * zoom}px;
								height: {imgNaturalHeight * zoom}px;
								transform: translate3d({panX}px, {panY}px, 0);
								cursor: {isDragging ? 'grabbing' : 'grab'};
							"
							draggable="false"
						/>
					{:else}
						<div class="crop-loading-indicator">Carregando imagem...</div>
					{/if}

					<!-- Moldura com grid de enquadramento -->
					<div class="crop-guidelines-box">
						<div class="grid-line h1"></div>
						<div class="grid-line h2"></div>
						<div class="grid-line v1"></div>
						<div class="grid-line v2"></div>
						<div class="corner-indicator tl"></div>
						<div class="corner-indicator tr"></div>
						<div class="corner-indicator bl"></div>
						<div class="corner-indicator br"></div>
					</div>
				</div>

				<!-- Barra de Controles de Zoom -->
				<div class="crop-toolbar">
					<button 
						type="button" 
						class="btn-zoom" 
						on:click={() => handleZoomChange(zoom - minZoom * 0.15)} 
						title="Reduzir Zoom"
						disabled={zoom <= minZoom}
					>
						<Icon name="minus" size={14} />
					</button>

					<div class="zoom-slider-wrap">
						<input 
							type="range" 
							min={minZoom} 
							max={maxZoom} 
							step={minZoom * 0.02}
							value={zoom}
							on:input={(e) => handleZoomChange(parseFloat(e.target.value))}
							class="zoom-slider"
						/>
					</div>

					<button 
						type="button" 
						class="btn-zoom" 
						on:click={() => handleZoomChange(zoom + minZoom * 0.15)} 
						title="Aumentar Zoom"
						disabled={zoom >= maxZoom}
					>
						<Icon name="plus" size={14} />
					</button>

					<button type="button" class="btn-reset-pan" on:click={handleReset} title="Centralizar Imagem">
						<span>Centralizar</span>
					</button>
				</div>
			</div>

			<div class="cropper-footer">
				<div class="footer-left">
					<button type="button" class="btn-secondary-link" on:click={onUseOriginal} title="Manter foto como está">
						<span>Usar Original Sem Corte</span>
					</button>
				</div>
				<div class="footer-right">
					<button type="button" class="btn-crop-cancel" on:click={onCancel}>
						Cancelar
					</button>
					<button type="button" class="btn-crop-confirm" on:click={executeCrop}>
						<Icon name="check" size={16} />
						<span>Aplicar Enquadramento</span>
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	.cropper-backdrop {
		position: fixed;
		inset: 0;
		background: rgba(11, 15, 25, 0.85);
		backdrop-filter: blur(6px);
		z-index: 99999;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 16px;
		animation: fadeIn 0.15s ease-out;
	}

	@keyframes fadeIn {
		from { opacity: 0; transform: scale(0.98); }
		to { opacity: 1; transform: scale(1); }
	}

	.cropper-modal-card {
		background: #FFFFFF;
		width: 100%;
		max-width: 440px;
		border-radius: 18px;
		box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.1);
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.cropper-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 18px 22px;
		border-bottom: 1px solid #E2E8F0;
		background: #F8FAFC;
	}

	.header-title-row {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.crop-icon-box {
		width: 38px;
		height: 38px;
		border-radius: 10px;
		background: #FFF7ED;
		border: 1px solid rgba(255, 106, 0, 0.2);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.crop-title {
		font-size: 15.5px;
		font-weight: 800;
		color: #0F172A;
		margin: 0 0 2px 0;
		line-height: 1.2;
	}

	.crop-subtitle {
		font-size: 11.5px;
		color: #64748B;
		margin: 0;
	}

	.btn-cropper-close {
		background: transparent;
		border: none;
		color: #94A3B8;
		cursor: pointer;
		width: 32px;
		height: 32px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: background 0.15s ease, color 0.15s ease;
	}

	.btn-cropper-close:hover {
		background: #E2E8F0;
		color: #0F172A;
	}

	.cropper-body {
		padding: 24px 22px;
		display: flex;
		flex-direction: column;
		align-items: center;
		background: #0F172A;
	}

	.crop-stage {
		position: relative;
		width: 280px;
		height: 280px;
		border-radius: 12px;
		overflow: hidden;
		background: #000000;
		box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
		user-select: none;
		touch-action: none;
	}

	.crop-draggable-img {
		position: absolute;
		top: 0;
		left: 0;
		will-change: transform;
		pointer-events: none;
	}

	.crop-loading-indicator {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #94A3B8;
		font-size: 13px;
	}

	.crop-guidelines-box {
		position: absolute;
		inset: 0;
		border: 2px solid #FF6A00;
		border-radius: 12px;
		pointer-events: none;
		box-shadow: 0 0 0 9999px rgba(11, 15, 25, 0.4);
	}

	.grid-line {
		position: absolute;
		background: rgba(255, 255, 255, 0.25);
		pointer-events: none;
	}

	.grid-line.h1 { top: 33.33%; left: 0; right: 0; height: 1px; }
	.grid-line.h2 { top: 66.66%; left: 0; right: 0; height: 1px; }
	.grid-line.v1 { left: 33.33%; top: 0; bottom: 0; width: 1px; }
	.grid-line.v2 { left: 66.66%; top: 0; bottom: 0; width: 1px; }

	.corner-indicator {
		position: absolute;
		width: 14px;
		height: 14px;
		border-color: #FFFFFF;
		border-style: solid;
	}

	.corner-indicator.tl { top: 4px; left: 4px; border-width: 3px 0 0 3px; border-top-left-radius: 4px; }
	.corner-indicator.tr { top: 4px; right: 4px; border-width: 3px 3px 0 0; border-top-right-radius: 4px; }
	.corner-indicator.bl { bottom: 4px; left: 4px; border-width: 0 0 3px 3px; border-bottom-left-radius: 4px; }
	.corner-indicator.br { bottom: 4px; right: 4px; border-width: 0 3px 3px 0; border-bottom-right-radius: 4px; }

	.crop-toolbar {
		margin-top: 18px;
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		max-width: 320px;
		background: #1E293B;
		padding: 6px 12px;
		border-radius: 12px;
		border: 1px solid rgba(255, 255, 255, 0.08);
	}

	.btn-zoom {
		background: transparent;
		border: none;
		color: #94A3B8;
		cursor: pointer;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.15s ease;
	}

	.btn-zoom:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.1);
		color: #FFFFFF;
	}

	.btn-zoom:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	.zoom-slider-wrap {
		flex: 1;
		display: flex;
		align-items: center;
	}

	.zoom-slider {
		width: 100%;
		height: 5px;
		accent-color: #FF6A00;
		cursor: pointer;
	}

	.btn-reset-pan {
		background: transparent;
		border: 1px solid rgba(255, 255, 255, 0.12);
		color: #CBD5E1;
		font-size: 11px;
		font-weight: 600;
		padding: 4px 8px;
		border-radius: 6px;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-reset-pan:hover {
		background: rgba(255, 255, 255, 0.08);
		color: #FFFFFF;
	}

	.cropper-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-top: 1px solid #E2E8F0;
		background: #FFFFFF;
		flex-wrap: wrap;
		gap: 12px;
	}

	.footer-left {
		display: flex;
		align-items: center;
	}

	.btn-secondary-link {
		background: transparent;
		border: none;
		color: #64748B;
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		padding: 4px 6px;
		text-decoration: underline;
		transition: color 0.15s ease;
	}

	.btn-secondary-link:hover {
		color: #0F172A;
	}

	.footer-right {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.btn-crop-cancel {
		background: #F1F5F9;
		border: 1px solid #CBD5E1;
		color: #475569;
		font-size: 13px;
		font-weight: 700;
		padding: 9px 16px;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-crop-cancel:hover {
		background: #E2E8F0;
		color: #0F172A;
	}

	.btn-crop-confirm {
		background: #FF6A00;
		border: none;
		color: #FFFFFF;
		font-size: 13px;
		font-weight: 800;
		padding: 9px 18px;
		border-radius: 8px;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		box-shadow: 0 2px 10px rgba(255, 106, 0, 0.35);
		transition: all 0.15s ease;
	}

	.btn-crop-confirm:hover {
		background: #E85A00;
		transform: translateY(-1px);
		box-shadow: 0 4px 14px rgba(255, 106, 0, 0.45);
	}
</style>
